/* eslint-disable max-lines -- Pre-existing violation */
import {
  Alert,
  Button,
  DataStatus,
  Dialog,
  Form,
  showNotification,
  useFormContext,
  type FormControl,
} from 'platform/components';
import {Box, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {useState} from 'react';
import {flushSync} from 'react-dom';
import {useWatch} from 'react-hook-form';
import {matchPath, useBeforeUnload, useBlocker, useLocation, useParams} from 'react-router-dom';

import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {
  useListFeatureQuery,
  useListMakeQuery,
  useListModelQuery,
  type FeatureResponseBody,
} from '@dms/api/new-vehicle';
import {
  ConfiguratorHero,
  ConfiguratorStepper,
  NewVehiclePageHeader,
  SummaryRail,
  type ConfiguratorHeroChip,
  type ConfiguratorHeroProps,
} from '@dms/features/new-vehicle';
import {vehiclesRoutes} from '@dms/routes';
import {usePermissions, useTenant} from '@dms/shared';

import {composePath, useNavigate, type ApiError} from 'shared/web';

import {NEW_VEHICLE_CONTENT_MAX_WIDTH} from '../NewVehicleShell/NEW_VEHICLE_CONTENT_MAX_WIDTH';
import {handleCatalogueError, t} from './catalogueUi';
import {
  draftCodeConflict,
  emptyNewModelDraft,
  newModelDraftKey,
  newModelDraftSchema,
  newModelSteps,
  NOT_STARTED,
  readNewModelDraft,
  reviewDraftCatalogueIdentity,
  reviewNewModelDraft,
  saveNewModelDraft,
  stepHasContent,
  type NewModelDraft,
  type YearDateField,
} from './newModelDraft';
import {NewModelReview} from './NewModelReview';
import {
  NewModelSteps,
  type CatalogueChoice,
  type ModelChoice,
  type NewModelFormValues,
} from './NewModelSteps';
import {useMaterializeNewModel, type StaleCheckpointLevel} from './useMaterializeNewModel';
import {WizardFooter} from './WizardFooter';

const formSchema = z.object({draft: newModelDraftSchema});

const STEP_SUBTITLES = [
  [
    'entity.newVehicle.authoring.makeSubtitle',
    'Choose the make this model belongs to, or create a new one.',
  ],
  [
    'entity.newVehicle.authoring.modelSubtitle',
    'Choose an existing model for the make, or create a new model.',
  ],
  [
    'entity.newVehicle.authoring.modelYearSubtitle',
    'Define the catalogue identity and validity period for this model year.',
  ],
  [
    'entity.newVehicle.authoring.enginesSubtitle',
    'Add the engines dealers can offer for this model year.',
  ],
  ['entity.newVehicle.authoring.trimsSubtitle', 'Add trim levels, prices and available engines.'],
  [
    'entity.newVehicle.authoring.coloursSubtitle',
    'Add exterior colours and assign them to trim levels.',
  ],
  [
    'entity.newVehicle.authoring.upholsterySubtitle',
    'Add upholstery choices and assign them to trim levels.',
  ],
  [
    'entity.newVehicle.authoring.equipmentSubtitle',
    'Attach equipment to each trim level as standard or optional. Package-only items are added through packages in step 9.',
  ],
  [
    'entity.newVehicle.authoring.packagesSubtitle',
    'Bundle equipment into packages with one price. Package-only items can only be ordered this way.',
  ],
  [
    'entity.newVehicle.authoring.reviewSubtitle',
    'Check the model before creating the catalogue draft.',
  ],
] as const;

interface WizardDraftProps {
  draftKey: string;
  canViewCost: boolean;
  currency: string;
  makes: CatalogueChoice[];
  models: ModelChoice[];
  features: FeatureResponseBody[];
}

function getStepFromParam(value: string | undefined) {
  return /^([1-9]|10)$/.test(value ?? '') ? Number(value) : 1;
}

function getRelativeSavedTime(savedAt: number | undefined) {
  if (savedAt === undefined) {
    return '';
  }
  const minutes = Math.max(0, Math.floor((Date.now() - savedAt) / 60_000));
  if (minutes < 1) {
    return t('entity.newVehicle.authoring.savedJustNow', 'just now');
  }
  if (minutes < 60) {
    return t('entity.newVehicle.authoring.savedMinutesAgo', '{count} min ago', {count: minutes});
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return t('entity.newVehicle.authoring.savedHoursAgo', '{count} h ago', {count: hours});
  }
  const days = Math.floor(hours / 24);
  return t('entity.newVehicle.authoring.savedDaysAgo', '{count} d ago', {count: days});
}

// The draft saves itself on every change, so the header carries no save or discard action. Leaving
// the page keeps the work; the resume prompt is where discarding it belongs.
function WizardHeader() {
  return (
    <NewVehiclePageHeader
      breadcrumbs={[
        {label: t('page.vehicles.title', 'Vehicles'), href: vehiclesRoutes.home},
        {
          label: t('entity.newVehicle.configurations.title', 'New vehicle configurations'),
          href: vehiclesRoutes.newVehicleConfigurations,
        },
        {
          label: t('entity.newVehicle.catalogue.title', 'Catalogue'),
          href: vehiclesRoutes.newVehicleCatalogue,
        },
        {label: t('entity.newVehicle.authoring.createNewModel', 'Create new model')},
      ]}
      title={t('entity.newVehicle.authoring.createVehicleModel', 'Create new vehicle model')}
      subtitle={t(
        'entity.newVehicle.authoring.wizardSubtitle',
        '10-step flow · author a model year for the catalogue'
      )}
    />
  );
}

// eslint-disable-next-line complexity, max-lines-per-function -- Pre-existing violation
function WizardContent(
  props: WizardDraftProps & {
    control: FormControl<NewModelFormValues>;
    storageFailed: boolean;
    persist: (draft: NewModelDraft, step: number) => boolean;
  }
) {
  const form = useFormContext<NewModelFormValues>();
  const draft = useWatch({control: props.control, name: 'draft'});
  const params = useParams();
  const step = getStepFromParam(params.step);
  const navigate = useNavigate();
  const location = useLocation();
  const materialize = useMaterializeNewModel();
  const [checkpointWarning, setCheckpointWarning] = useState(false);
  const [staleCheckpoint, setStaleCheckpoint] = useState<StaleCheckpointLevel | null>(null);
  const [confirmStartOver, setConfirmStartOver] = useState(false);
  const [materializedModelYearId, setMaterializedModelYearId] = useState(
    draft.materialization.modelYearId
  );
  const [saving, setSaving] = useState(false);
  const navigationBlocker = useBlocker(
    ({currentLocation, nextLocation}) =>
      saving &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search ||
        currentLocation.hash !== nextLocation.hash)
  );
  useBeforeUnload((event) => {
    if (!saving) {
      return;
    }
    event.preventDefault();
    event.returnValue = '';
  });
  const [invalidDateInputs, setInvalidDateInputs] = useState<YearDateField[]>([]);
  const reviewDraft = (current: NewModelDraft) =>
    [
      ...reviewNewModelDraft(current, invalidDateInputs),
      ...reviewDraftCatalogueIdentity(current, props.makes, props.models),
    ].sort((a, b) => a.step - b.step);
  const blockers = reviewDraft(draft);
  const currentIssues = blockers.filter((issue) => issue.step === step);
  const earlierBlockedStep = blockers.find((issue) => issue.step < step)?.step;
  const isLocked = saving || draft.materialization.status !== 'not-started';
  const publicationGatePassed = blockers.length === 0;
  const steps = newModelSteps().map((title, index) => {
    const stepNumber = index + 1;
    // Judge a step once it is behind the author or once they have put something into it. Every
    // required step is blocked while empty, so judging untouched ones would mark most of a fresh
    // draft as broken and teach authors to ignore the marker.
    const isJudged = index < step - 1 || stepHasContent(draft, stepNumber);
    return {
      title,
      isDisabled: stepNumber > step && blockers.some((issue) => issue.step < stepNumber),
      isComplete: isJudged ? !blockers.some((issue) => issue.step === stepNumber) : undefined,
    };
  });
  const footerIssue =
    currentIssues.length > 0 // eslint-disable-line no-nested-ternary -- Pre-existing violation
      ? currentIssues.map((issue) => issue.message).join(' ')
      : earlierBlockedStep !== undefined
        ? t(
            'entity.newVehicle.catalogue.validation.earlierStepBlocked',
            'Fix step {step} before continuing.',
            {step: earlierBlockedStep}
          )
        : undefined;
  const makeName =
    draft.make.mode === 'new'
      ? draft.make.name
      : props.makes.find((row) => row.id === draft.make.id)?.name;
  const modelName =
    draft.model.mode === 'new'
      ? draft.model.name
      : props.models.find((row) => row.id === draft.model.id)?.name;
  const chips: ConfiguratorHeroChip[] = [
    ...(draft.engines.length > 0
      ? [
          {
            caption: t('entity.newVehicle.catalogue.engines', 'Engines'),
            label: String(draft.engines.length),
          },
        ]
      : []),
    ...(draft.trims.length > 0
      ? [
          {
            caption: t('entity.newVehicle.catalogue.trimLevels', 'Trim levels'),
            label: String(draft.trims.length),
          },
        ]
      : []),
    ...(draft.colours.length > 0
      ? [
          {
            caption: t('entity.newVehicle.authoring.colors', 'Colours'),
            label: String(draft.colours.length),
          },
        ]
      : []),
    ...(draft.upholsteries.length > 0
      ? [
          {
            caption: t('entity.newVehicle.catalogue.upholstery', 'Upholstery'),
            label: String(draft.upholsteries.length),
          },
        ]
      : []),
    ...(draft.attachments.length > 0
      ? [
          {
            caption: t('entity.newVehicle.catalogue.equipment', 'Equipment'),
            label: String(draft.attachments.length),
          },
        ]
      : []),
    ...(draft.packages.length > 0
      ? [
          {
            caption: t('entity.newVehicle.catalogue.packages', 'Packages'),
            label: String(draft.packages.length),
          },
        ]
      : []),
  ];
  const heroStatus: ConfiguratorHeroProps['status'] = match({
    status: draft.materialization.status,
    publicationGatePassed,
    saving,
  })
    .with({status: 'complete'}, () => ({
      label: t('entity.newVehicle.authoring.draftCreated.short', 'Draft created'),
      colorScheme: 'green' as const,
      isSubtle: true,
    }))
    .with({status: 'started', saving: true}, () => ({
      label: t('entity.newVehicle.authoring.creationInProgress', 'Creation in progress'),
      colorScheme: 'orange' as const,
      isSubtle: true,
    }))
    .with({status: 'started'}, () => ({
      label: t(
        'entity.newVehicle.authoring.creationInterrupted',
        'Creation interrupted — resume to finish'
      ),
      colorScheme: 'orange' as const,
      isSubtle: true,
    }))
    .with({status: 'not-started', publicationGatePassed: true}, () => ({
      label: t('entity.newVehicle.authoring.readyToCreate', 'Ready to create the draft'),
      colorScheme: 'green' as const,
      isSubtle: true,
    }))
    .otherwise(() => undefined);
  const remainingStepCount = new Set(blockers.map((issue) => issue.step)).size;
  const goTo = (next: number) => {
    if (!saving) {
      props.persist(form.getValues('draft'), next);
    }
    navigate(
      `${composePath(vehiclesRoutes.newVehicleCatalogueNewModel, {
        params: {step: String(next)},
      })}${location.hash}`,
      {replace: true}
    );
  };
  const save = async () => {
    setSaving(true);
    setStaleCheckpoint(null);
    try {
      const {modelYearId} = await materialize(
        form.getValues('draft'),
        props.canViewCost,
        (state) => {
          const current = {...form.getValues('draft'), materialization: state};
          setMaterializedModelYearId(state.modelYearId);
          if (!props.persist(current, step)) {
            setCheckpointWarning(state.modelYearId !== null);
            throw new Error('Cannot save authoring checkpoint.');
          }
          form.setValue('draft.materialization', state);
        },
        setStaleCheckpoint
      );
      showNotification.success(
        t('entity.newVehicle.authoring.draftCreated.short', 'Draft created'),
        {
          hyperlinks: [
            {
              title: t('entity.newVehicle.catalogue.openModelYear', 'Open model year'),
              onClick: () => openModelYear(modelYearId),
            },
          ],
        }
      );
    } catch (error) {
      const codeConflict = draftCodeConflict(form.getValues('draft'));
      if (codeConflict) {
        // The navigation blocker holds while saving, so the attempt has to render as over first.
        flushSync(() => setSaving(false));
        goTo(codeConflict.step);
      } else if (!(error instanceof Error)) {
        handleCatalogueError(error as ApiError);
      }
    } finally {
      setSaving(false);
    }
  };
  const startOver = () => {
    const current = {...form.getValues('draft'), materialization: NOT_STARTED};
    props.persist(current, step);
    form.setValue('draft.materialization', NOT_STARTED, {shouldDirty: true});
    setMaterializedModelYearId(null);
    setStaleCheckpoint(null);
    setConfirmStartOver(false);
  };
  const resumable = draft.materialization.status === 'started' && !saving;
  const continueBlockedNavigation = () => {
    if (navigationBlocker.state !== 'blocked') {
      return;
    }
    const authoringRoute = matchPath(
      vehiclesRoutes.newVehicleCatalogueNewModel,
      navigationBlocker.location.pathname
    );
    const blockedStep = authoringRoute?.params.step;
    if (blockedStep && String(getStepFromParam(blockedStep)) === blockedStep) {
      props.persist(form.getValues('draft'), getStepFromParam(blockedStep));
    }
    navigationBlocker.proceed();
  };
  const openModelYear = (modelYearId: string) =>
    navigate(
      composePath(vehiclesRoutes.newVehicleCatalogueModelYear, {
        params: {modelYearId, tab: 'trim-levels'},
      })
    );
  // Every reason the footer's Create button is disabled must be legible next to it. Validation
  // blockers already arrive as `footerIssue`; `saving` shows as the button's loading state.
  // Offer the created draft in place of Create, but only once the attempt actually finished. An
  // interrupted one still needs Retry or Start over, and mid-flight the Create button is what
  // reports progress. A failed checkpoint write still means the draft itself was created.
  const showInspect =
    !saving &&
    materializedModelYearId !== null &&
    (draft.materialization.status === 'complete' || checkpointWarning);
  const storageBlocker = props.storageFailed // eslint-disable-line no-nested-ternary -- Pre-existing violation
    ? step === 10
      ? t(
          'entity.newVehicle.authoring.createBlockedByStorage',
          "Can't create until this browser can save a recovery checkpoint. Keep this page open."
        )
      : t(
          'entity.newVehicle.authoring.storageUnavailableHint',
          "This browser can't save recovery changes right now. Keep this page open."
        )
    : undefined;
  const materializationHint = match({step, status: draft.materialization.status, saving})
    .with({step: 10, status: 'started', saving: false}, () =>
      t(
        'entity.newVehicle.authoring.createBlockedByInterruption',
        'Creation was interrupted. Use Retry or Start over above.'
      )
    )
    .otherwise(() => undefined);

  return (
    <VStack spacing={5}>
      <WizardHeader />
      <ConfiguratorStepper
        label={t('entity.newVehicle.authoring.wizardSteps', 'Create model steps')}
        steps={steps}
        currentIndex={step - 1}
        onStepChange={(index) => goTo(index + 1)}
        data-testid="new-model-wizard"
      />
      <ConfiguratorHero
        makeName={makeName}
        modelName={modelName}
        modelYearName={draft.year.code || undefined}
        placeholder={t(
          'entity.newVehicle.authoring.heroPlaceholder',
          'Name the make and model to start'
        )}
        hint={
          modelName
            ? undefined
            : t(
                'entity.newVehicle.authoring.heroHint',
                'Build the model year step by step — engines, trim levels, colours, upholstery, equipment and packages.'
              )
        }
        chips={chips}
        status={heroStatus}
        data-testid="new-model-wizard"
      />
      <HStack spacing={5} align="flex-start">
        <Box flex={1} minWidth={0}>
          <VStack spacing={4}>
            <Show when={resumable}>
              <Alert
                variant="error"
                data-testid="new-model-wizard-materialization-error"
                title={t(
                  'entity.newVehicle.authoring.createFailed',
                  "We couldn't create the catalogue draft. Your changes are still saved in this browser."
                )}
                message={
                  // A refused code is reported on its row and in the footer, and goes stale once fixed.
                  (draft.materialization.codeConflict ? null : draft.materialization.lastError) ??
                  t(
                    'entity.newVehicle.catalogue.validation.resumeHint',
                    'A previous attempt did not finish. Retry to continue from the last saved step, or start over to create everything again.'
                  )
                }
              />
              <HStack spacing={3}>
                <Button
                  title={t('general.actions.retry', 'Retry')}
                  aria-label={t('general.actions.retry', 'Retry')}
                  leftIcon="navigation/refresh"
                  isDisabled={props.storageFailed || !publicationGatePassed}
                  onClick={() => void save()}
                />
                <Button
                  variant="secondary"
                  title={t('entity.newVehicle.catalogue.validation.startOver', 'Start over')}
                  onClick={() => setConfirmStartOver(true)}
                />
              </HStack>
            </Show>
            <Show when={checkpointWarning}>
              <Alert
                variant="warning"
                message={t(
                  'entity.newVehicle.authoring.checkpointNotSaved',
                  "The model-year draft was created, but this browser couldn't save its recovery checkpoint. Keep this page open and inspect the created draft."
                )}
              />
            </Show>
            <Show when={staleCheckpoint !== null}>
              <Alert
                variant="warning"
                data-testid="new-model-wizard-stale-checkpoint"
                message={t(
                  'entity.newVehicle.authoring.staleCheckpointReplaced',
                  '{entity} from the previous attempt was archived, removed or moved, so a new one was created.',
                  {
                    entity: match(staleCheckpoint)
                      .with('make', () => t('entity.vehicle.labels.make', 'Make'))
                      .with('model', () => t('entity.vehicle.labels.model', 'Model'))
                      .otherwise(() => t('entity.newVehicle.labels.modelYear', 'Model year')),
                  }
                )}
              />
            </Show>
            <Show when={draft.materialization.status === 'complete'}>
              <Alert
                variant="success"
                message={t(
                  'entity.newVehicle.authoring.draftCreated',
                  'Model-year draft created. Continue in the model-year editor to review readiness.'
                )}
              />
            </Show>
            <Box
              backgroundColor="general.white"
              border="1px solid"
              borderColor="general.separator"
              borderRadius="large"
              padding={6}
            >
              <VStack spacing={5}>
                <VStack spacing={1}>
                  <Heading
                    size={3}
                  >{`${t('entity.newVehicle.authoring.step', 'Step')} ${step} — ${newModelSteps()[step - 1]}`}</Heading>
                  <Text size="small" color="secondary">
                    {t(STEP_SUBTITLES[step - 1][0], STEP_SUBTITLES[step - 1][1])}
                  </Text>
                </VStack>
                {step === 10 ? (
                  <NewModelReview
                    draft={draft}
                    makes={props.makes}
                    models={props.models}
                    currency={props.currency}
                    blockers={blockers}
                    goTo={goTo}
                  />
                ) : (
                  <NewModelSteps
                    {...props}
                    isLocked={isLocked}
                    step={step}
                    goTo={goTo}
                    onInvalidDateInput={(field, isInvalid) =>
                      setInvalidDateInputs((fields) => {
                        // The field reports on every focus and commit; skip re-renders when unchanged.
                        if (fields.includes(field) === isInvalid) {
                          return fields;
                        }
                        return isInvalid
                          ? [...fields, field]
                          : fields.filter((value) => value !== field);
                      })
                    }
                  />
                )}
              </VStack>
            </Box>
          </VStack>
        </Box>
        <Show when={step !== 10}>
          <Box width={[72, 72, 76, 84]} flexShrink={0}>
            <SummaryRail
              title={t('general.labels.summary', 'Summary')}
              rows={[
                {label: t('entity.vehicle.labels.make', 'Make'), value: makeName || undefined},
                {label: t('entity.vehicle.labels.model', 'Model'), value: modelName || undefined},
                {
                  label: t('entity.newVehicle.labels.modelYear', 'Model year'),
                  value: draft.year.code || undefined,
                },
                {
                  label: t('entity.newVehicle.catalogue.engines', 'Engines'),
                  value: draft.engines.length > 0 ? String(draft.engines.length) : undefined,
                },
                {
                  label: t('entity.newVehicle.catalogue.trimLevels', 'Trim levels'),
                  value: draft.trims.length > 0 ? String(draft.trims.length) : undefined,
                },
                {
                  label: t('entity.newVehicle.authoring.colors', 'Colours'),
                  value: draft.colours.length > 0 ? String(draft.colours.length) : undefined,
                },
                {
                  label: t('entity.newVehicle.catalogue.upholstery', 'Upholstery'),
                  value:
                    draft.upholsteries.length > 0 ? String(draft.upholsteries.length) : undefined,
                },
                {
                  label: t('entity.newVehicle.catalogue.equipment', 'Equipment'),
                  value:
                    draft.attachments.length > 0 ? String(draft.attachments.length) : undefined,
                },
                {
                  label: t('entity.newVehicle.catalogue.packages', 'Packages'),
                  value: draft.packages.length > 0 ? String(draft.packages.length) : undefined,
                },
              ]}
              footer={
                remainingStepCount > 0 ? (
                  <Text size="small" color="secondary">
                    {t(
                      'entity.newVehicle.authoring.remainingRequired',
                      'Required steps remaining: {count}',
                      {count: remainingStepCount}
                    )}
                  </Text>
                ) : (
                  <HStack align="center" spacing={2}>
                    <Icon value="navigation/check" color="text.success" size={4} />
                    <Text size="small">
                      {t('entity.newVehicle.authoring.readyToCreate', 'Ready to create the draft')}
                    </Text>
                  </HStack>
                )
              }
            />
          </Box>
        </Show>
      </HStack>
      <Dialog
        isOpen={navigationBlocker.state === 'blocked'}
        size="small"
        title={
          saving
            ? t('entity.newVehicle.authoring.creationNavigationBlocked', 'Creation in progress')
            : t('entity.newVehicle.authoring.creationFinished', 'Creation finished')
        }
        onClose={() => navigationBlocker.reset?.()}
        buttons={[
          {
            variant: 'secondary',
            title: t('entity.newVehicle.authoring.stayUntilCreationFinishes', 'Stay on this page'),
            onClick: () => navigationBlocker.reset?.(),
          },
          ...(saving
            ? []
            : [
                {
                  variant: 'primary' as const,
                  title: t(
                    'entity.newVehicle.authoring.continueBlockedNavigation',
                    'Continue where you were going'
                  ),
                  onClick: continueBlockedNavigation,
                },
              ]),
        ]}
      >
        <Text>
          {saving
            ? t(
                'entity.newVehicle.authoring.creationNavigationBlockedMessage',
                'Creation in progress — leaving now could lose the created draft link.'
              )
            : t(
                'entity.newVehicle.authoring.creationFinishedMessage',
                'The creation attempt has finished. Continue where you were going or stay on this page.'
              )}
        </Text>
      </Dialog>
      <Dialog
        isOpen={confirmStartOver}
        size="small"
        title={t('entity.newVehicle.catalogue.validation.startOver', 'Start over')}
        onClose={() => setConfirmStartOver(false)}
        buttons={[
          {
            variant: 'secondary',
            title: t('general.actions.cancel', 'Cancel'),
            onClick: () => setConfirmStartOver(false),
          },
          {
            variant: 'danger',
            title: t('entity.newVehicle.catalogue.validation.startOver', 'Start over'),
            onClick: startOver,
          },
        ]}
      >
        <Text>
          {t(
            'entity.newVehicle.catalogue.validation.startOverWarning',
            'The next attempt creates every record again. Records already created in the catalogue are kept and are not changed.'
          )}
        </Text>
      </Dialog>
      <WizardFooter
        step={step}
        issue={footerIssue}
        // Continue only navigates, like the stepper, so a started attempt still lets the author move on.
        isContinueDisabled={saving || blockers.some((issue) => issue.step <= step)}
        isCreateDisabled={
          saving ||
          props.storageFailed ||
          !publicationGatePassed ||
          draft.materialization.status !== 'not-started'
        }
        isCreating={saving}
        storageFailed={props.storageFailed}
        // The hint explains why Create is unavailable, so it goes quiet whenever Create is not the
        // action on offer.
        hint={showInspect ? undefined : (storageBlocker ?? materializationHint)}
        onBack={() => goTo(step - 1)}
        onContinue={() => {
          const latestIssue = reviewDraft(form.getValues('draft')).find(
            (issue) => issue.step === step
          );
          if (!latestIssue) {
            goTo(step + 1);
          }
        }}
        onCreate={() => void save()}
        onInspect={showInspect ? () => openModelYear(materializedModelYearId ?? '') : undefined}
      />
    </VStack>
  );
}

export function NewModelWizardDraft(props: WizardDraftProps) {
  const navigate = useNavigate();
  const params = useParams();
  const [stored] = useState(() => readNewModelDraft(props.draftKey));
  const [choice, setChoice] = useState<'pending' | 'resume' | 'new'>(
    stored.status === 'saved' || stored.status === 'invalid' ? 'pending' : 'new'
  );
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [generation, setGeneration] = useState(0);
  const [storageFailed, setStorageFailed] = useState(stored.status === 'unavailable');
  const persist = (draft: NewModelDraft, step: number) => {
    const saved = saveNewModelDraft(props.draftKey, draft, step);
    setStorageFailed(!saved);
    return saved;
  };
  const discard = () => {
    try {
      sessionStorage.removeItem(props.draftKey);
    } catch {
      setStorageFailed(true);
      return;
    }
    setChoice('new');
    setGeneration((value) => value + 1);
    setConfirmDiscard(false);
    navigate(composePath(vehiclesRoutes.newVehicleCatalogueNewModel, {params: {step: '1'}}));
  };
  const savedIdentity =
    stored.status === 'saved'
      ? `${stored.draft.make.name || props.makes.find((row) => row.id === stored.draft.make.id)?.name || t('entity.newVehicle.authoring.unnamedMake', 'Unnamed make')} ${stored.draft.model.name || props.models.find((row) => row.id === stored.draft.model.id)?.name || t('entity.newVehicle.authoring.unnamedModel', 'unnamed model')}`
      : '';
  const savedTime = stored.status === 'saved' ? getRelativeSavedTime(stored.savedAt) : '';

  return (
    <HStack justify="center" width="100%">
      <Box width="100%" maxWidth={NEW_VEHICLE_CONTENT_MAX_WIDTH}>
        <VStack spacing={5}>
          <Show when={choice === 'pending'}>
            <WizardHeader />
          </Show>
          <Show key={generation} when={!(choice === 'pending')}>
            <Form<NewModelFormValues>
              experimentalZodSchema={formSchema}
              defaultValues={{
                draft:
                  choice === 'resume' && stored.status === 'saved'
                    ? stored.draft
                    : emptyNewModelDraft(),
              }}
              onChange={(values) => persist(values.draft, getStepFromParam(params.step))}
            >
              {(control) => (
                <WizardContent
                  {...props}
                  control={control}
                  storageFailed={storageFailed}
                  persist={persist}
                />
              )}
            </Form>
          </Show>
          <Dialog
            isOpen={choice === 'pending' && !confirmDiscard}
            title={t(
              'entity.newVehicle.authoring.continueSavedDraft',
              'Continue where you left off?'
            )}
            onClose={() => undefined}
            hideCloseButton
            buttons={[
              {
                variant: 'secondary',
                title: t('entity.newVehicle.actions.startNew', 'Start new'),
                onClick: () => setConfirmDiscard(true),
              },
              ...(stored.status === 'saved'
                ? [
                    {
                      variant: 'primary' as const,
                      title: t('entity.newVehicle.authoring.resumeSaved', 'Resume'),
                      onClick: () => {
                        setChoice('resume');
                        if (stored.status === 'saved') {
                          navigate(
                            composePath(vehiclesRoutes.newVehicleCatalogueNewModel, {
                              params: {step: String(stored.step)},
                            })
                          );
                        }
                      },
                    },
                  ]
                : []),
            ]}
          >
            <Text>
              {stored.status === 'invalid'
                ? t(
                    'entity.newVehicle.authoring.invalidDraft',
                    'The work saved in this browser cannot be opened by this version. Start new only if you want to discard it.'
                  )
                : t(
                    'entity.newVehicle.authoring.resumeBrowserDraft',
                    'You have unfinished work on {name} saved in this browser{savedTime}.',
                    {
                      name: savedIdentity,
                      savedTime: savedTime
                        ? ' ' +
                          t('entity.newVehicle.authoring.lastEdited', '(last edited {savedTime})', {
                            savedTime,
                          })
                        : '',
                    }
                  )}
            </Text>
          </Dialog>
          <Dialog
            isOpen={confirmDiscard}
            title={t('entity.newVehicle.authoring.confirmDiscard', 'Discard your changes?')}
            onClose={() => setConfirmDiscard(false)}
            buttons={[
              {
                variant: 'secondary',
                title: t('general.actions.cancel', 'Cancel'),
                onClick: () => setConfirmDiscard(false),
              },
              {
                variant: 'danger',
                title: t(
                  'entity.newVehicle.authoring.confirmDiscardAction',
                  'Discard and start new'
                ),
                onClick: discard,
              },
            ]}
          >
            <Text>
              {t(
                'entity.newVehicle.authoring.discardWarning',
                'This removes the changes saved in this browser. Catalogue records already created are not changed.'
              )}
            </Text>
          </Dialog>
        </VStack>
      </Box>
    </HStack>
  );
}

export function NewModelWizard() {
  const [canWrite, canViewCost] = usePermissions({
    permissionKeys: ['newVehicleCatalogueWrite', 'newVehicleCatalogueViewCost'],
  });
  const user = useGetCurrentUserInfoQuery();
  const tenant = useTenant();
  const makes = useListMakeQuery({}, {skip: !canWrite});
  const models = useListModelQuery({}, {skip: !canWrite});
  const features = useListFeatureQuery({}, {skip: !canWrite});
  if (!canWrite) {
    return (
      <Alert
        variant="warning"
        message={t(
          'entity.newVehicle.authoring.writeRequired',
          'Catalogue write permission is required to create a model.'
        )}
      />
    );
  }
  return (
    <DataStatus
      isLoading={
        user.isLoading ||
        tenant.isLoading ||
        makes.isLoading ||
        models.isLoading ||
        features.isLoading
      }
      isError={
        user.isError || tenant.isError || makes.isError || models.isError || features.isError
      }
      errorAction={{
        title: t('general.actions.retry', 'Retry'),
        onClick: () => {
          void user.refetch();
          void makes.refetch();
          void models.refetch();
          void features.refetch();
        },
      }}
    >
      <Show when={user.data && tenant.tenant ? {user: user.data, tenant: tenant.tenant} : null}>
        {(session) => (
          <NewModelWizardDraft
            key={`${session.tenant.id}:${session.user.id}`}
            draftKey={newModelDraftKey(session.tenant.id, session.user.id)}
            canViewCost={canViewCost}
            currency={tenant.tenantCurrency}
            // Archived records stay in: their names still block duplicates. Pickers hide them.
            makes={makes.data?.items ?? []}
            models={models.data?.items ?? []}
            features={
              features.data?.items.filter(
                (row) =>
                  !row.archived &&
                  (row.kind === 'option' || row.kind === 'wheel' || row.kind === 'accessory')
              ) ?? []
            }
          />
        )}
      </Show>
    </DataStatus>
  );
}
